/* eslint-disable react-doctor/only-export-components */
import {
    isVideoMedia,
    LISTING_IMAGE_DELIVERY_WIDTHS,
    LISTING_IMAGE_MASTER_WIDTH,
    listingMediaDeliveryUrl,
    isTrustedListingPhotoUrl,
    normalizeRemoteMediaUrl,
} from "@wags/utils/media.ts";
import type React from "react";
import { useState } from "react";
import { cn } from "../../lib/utils.ts";
import { VideoWithFallback } from "./video-with-fallback.tsx";

declare const __DEV__: boolean | undefined;

const imageWidths = [
    16, 32, 48, 64, 96, 256, 384, 640, 1080, 1200, 1920,
] as const;

type ImgWidth = (typeof imageWidths)[number];

const getVercelOptimizedUrl = (url: string, width: ImgWidth) => {
    const searchParams = new URLSearchParams();
    searchParams.append("url", url);
    searchParams.append("w", width.toString());
    searchParams.append("q", "75");
    return `/_vercel/image?${searchParams.toString()}`;
};

const isDev = () => {
    try {
        // oxlint-disable-next-line typescript/no-explicit-any -- Don't want vite specific types here
        return (import.meta as any).env?.DEV === true;
    } catch {
        return typeof __DEV__ !== "undefined" ? __DEV__ : false;
    }
};

const snapWidth = (w: number): ImgWidth =>
    imageWidths.find((candidate) => candidate >= w) ??
    imageWidths[imageWidths.length - 1];

const snapListingImageWidth = (
    width: number,
): (typeof LISTING_IMAGE_DELIVERY_WIDTHS)[number] =>
    LISTING_IMAGE_DELIVERY_WIDTHS.find((candidate) => candidate >= width) ??
    LISTING_IMAGE_MASTER_WIDTH;

function getDirectListingImageProps(
    src: string,
    width: number,
    height: number,
) {
    const directUrl = listingMediaDeliveryUrl(
        src,
        snapListingImageWidth(width * 2),
    );
    if (!directUrl) return null;
    return {
        src: directUrl,
        width,
        height,
    };
}

type DirectListingImageProps = NonNullable<
    ReturnType<typeof getDirectListingImageProps>
>;

function getDirectListingImageMasterFallbackProps(
    src: string,
    width: number,
    height: number,
) {
    const masterUrl = listingMediaDeliveryUrl(src, LISTING_IMAGE_MASTER_WIDTH);
    if (!masterUrl) return null;
    return {
        src: masterUrl,
        width,
        height,
    };
}

function getDirectListingImageMasterFallbackForFailure(
    src: string,
    width: number,
    height: number,
    directProps: DirectListingImageProps | null,
    failedDirectSrc: string | null,
) {
    if (!directProps || directProps.src === src || failedDirectSrc !== src) {
        return null;
    }
    return getDirectListingImageMasterFallbackProps(src, width, height);
}

type DirectImageFailureState = {
    directMasterFallbackProps: ReturnType<
        typeof getDirectListingImageMasterFallbackProps
    > | null;
    directProps: DirectListingImageProps | null;
    normalizedSrc: string;
    setErroredSrc: React.Dispatch<React.SetStateAction<string | null>>;
    setFailedDirectSrc: React.Dispatch<React.SetStateAction<string | null>>;
};

function advanceDirectImageFailure(input: DirectImageFailureState) {
    if (input.directMasterFallbackProps) {
        input.setErroredSrc(input.normalizedSrc);
        return;
    }
    if (input.directProps && input.directProps.src !== input.normalizedSrc) {
        input.setFailedDirectSrc(input.normalizedSrc);
        return;
    }
    input.setErroredSrc(input.normalizedSrc);
}

function handleVercelImageError(
    event: React.SyntheticEvent<HTMLImageElement>,
    imageOnError: React.ReactEventHandler<HTMLImageElement> | undefined,
    input: DirectImageFailureState,
) {
    imageOnError?.(event);
    advanceDirectImageFailure(input);
}

function getImageProps(input: {
    directMasterFallbackProps: ReturnType<
        typeof getDirectListingImageMasterFallbackProps
    > | null;
    directProps: DirectListingImageProps | null;
    height: number;
    normalizedSrc: string;
    listingMedia: boolean;
    trustedListingMedia: boolean;
    unoptimized: boolean;
    width: number;
}) {
    if (input.directMasterFallbackProps) {
        return input.directMasterFallbackProps;
    }
    if (input.directProps) {
        return input.directProps;
    }
    if (input.listingMedia && input.trustedListingMedia) {
        return {
            src: input.normalizedSrc,
            width: input.width,
            height: input.height,
        };
    }
    if (input.unoptimized) {
        return {
            src: input.normalizedSrc,
            width: input.width,
            height: input.height,
        };
    }
    return getVercelOptimizedImageProps(
        input.normalizedSrc,
        input.width,
        input.height,
    );
}

function shouldUseImageFallback(input: {
    directProps: DirectListingImageProps | null;
    errored: boolean;
    listingMedia: boolean;
    mimeType?: string | null;
    normalizedSrc: string;
    trustedListingMedia: boolean;
}) {
    return (
        input.errored ||
        !input.normalizedSrc ||
        (input.listingMedia &&
            !input.directProps &&
            !input.trustedListingMedia &&
            !isVideoMedia({
                mimeType: input.mimeType,
                url: input.normalizedSrc,
            }))
    );
}

export function listingImageSrc(src: string, width: number) {
    const normalized = normalizeRemoteMediaUrl(src) ?? src;
    const direct = getDirectListingImageProps(normalized, width, width);
    if (direct) return direct.src;
    return isTrustedListingPhotoUrl(normalized) ? normalized : null;
}

export function optimizedImageUrl(src: string, width: number) {
    const directUrl = listingMediaDeliveryUrl(
        src,
        snapListingImageWidth(width),
    );
    if (directUrl) return directUrl;
    return isDev() ? src : getVercelOptimizedUrl(src, snapWidth(width));
}

export const getVercelOptimizedImageProps = (
    src: string,
    width: number,
    height: number,
) => {
    const directProps = getDirectListingImageProps(src, width, height);
    if (directProps) return directProps;
    if (isDev()) {
        return { src, width, height };
    }

    const widths = [...new Set([width, width * 2].map(snapWidth))].sort(
        (a, b) => a - b,
    );

    const srcSet = widths
        .map((w) => `${getVercelOptimizedUrl(src, w)} ${w}w`)
        .join(", ");

    const sizes = `(max-width: ${width}px) 100vw, ${width}px`;

    return {
        src: getVercelOptimizedUrl(src, widths[0]),
        srcSet,
        sizes,
        width,
        height,
    };
};

export function panelFillBackgroundImage(
    src: string,
    listingMedia = false,
): string {
    const normalized = normalizeRemoteMediaUrl(src) ?? src;
    if (!normalized) return "none";
    const directUrl = listingMediaDeliveryUrl(normalized, 32);
    if (directUrl) return `url(${JSON.stringify(directUrl)})`;
    if (listingMedia) {
        return isTrustedListingPhotoUrl(normalized)
            ? `url(${JSON.stringify(normalized)})`
            : "none";
    }
    if (isDev()) return `url(${JSON.stringify(normalized)})`;
    return `url(${JSON.stringify(getVercelOptimizedUrl(normalized, 32))})`;
}

export type VercelImageProps = Omit<
    React.ImgHTMLAttributes<HTMLImageElement>,
    "src" | "srcSet" | "loading" | "sizes"
> & {
    alt: string;
    src: string;
    width: number;
    height?: number;
    loading?: React.ImgHTMLAttributes<HTMLImageElement>["loading"];

    priority?: boolean;
    unoptimized?: boolean;
    listingMedia?: boolean;
    mimeType?: string | null;
    videoMode?: "autoplay" | "thumbnail";
    loadingPlaceholder?: React.ReactNode;
    ref?: React.Ref<HTMLImageElement>;
};

export function ImageErrorFallback({
    alt,
    className,
}: {
    alt: string;
    className?: string;
}) {
    return (
        <div
            {...(alt ? { role: "img", "aria-label": alt } : {})}
            aria-hidden={alt ? undefined : true}
            data-revealed=""
            className={cn(
                className,
                "flex items-center justify-center bg-havanese text-center text-body-small text-doberman-black/60",
            )}
        >
            {alt && <span>Photo unavailable</span>}
        </div>
    );
}

export function VercelImage({
    alt,
    src,
    width,
    height,
    loading = "lazy",
    priority = false,
    unoptimized = false,
    listingMedia = false,
    className,
    mimeType,
    videoMode = "autoplay",
    loadingPlaceholder,
    ...rest
}: VercelImageProps) {
    const normalizedSrc = normalizeRemoteMediaUrl(src) ?? src;
    const imageHeight = height || width;
    const trustedListingMedia =
        listingMedia && isTrustedListingPhotoUrl(normalizedSrc);

    const [erroredSrc, setErroredSrc] = useState<string | null>(null);
    const [failedDirectSrc, setFailedDirectSrc] = useState<string | null>(null);
    const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
    const errored = erroredSrc === normalizedSrc;
    const directProps =
        unoptimized && !listingMedia
            ? null
            : getDirectListingImageProps(normalizedSrc, width, imageHeight);
    const directMasterFallbackProps =
        getDirectListingImageMasterFallbackForFailure(
            normalizedSrc,
            width,
            imageHeight,
            directProps,
            failedDirectSrc,
        );

    const {
        ref: forwardedRef,
        onError: imageOnError,
        onLoad: imageOnLoad,
        ...imgRest
    } = rest;
    const checkAlreadyErrored = (node: HTMLImageElement | null) => {
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
        if (loadingPlaceholder && node?.complete && node.naturalWidth > 0) {
            setLoadedSrc(normalizedSrc);
        }
        if (node?.complete && node.naturalWidth === 0) {
            advanceDirectImageFailure({
                directMasterFallbackProps,
                directProps,
                normalizedSrc,
                setErroredSrc,
                setFailedDirectSrc,
            });
        }
    };

    if (
        shouldUseImageFallback({
            directProps,
            errored,
            listingMedia,
            mimeType,
            normalizedSrc,
            trustedListingMedia,
        })
    ) {
        return <ImageErrorFallback alt={alt} className={className} />;
    }

    if (isVideoMedia({ mimeType, url: normalizedSrc })) {
        const videoProps =
            rest as React.VideoHTMLAttributes<HTMLVideoElement> & {
                ref?: React.Ref<HTMLVideoElement>;
            };
        return (
            <VideoWithFallback
                src={normalizedSrc}
                aria-label={alt}
                width={width}
                height={imageHeight}
                muted
                autoPlay={videoMode === "autoplay"}
                loop={videoMode === "autoplay"}
                playsInline
                preload={videoMode === "autoplay" ? "metadata" : "auto"}
                className={className}
                {...videoProps}
                fallback={
                    <ImageErrorFallback alt={alt} className={className} />
                }
            />
        );
    }

    const optimizedProps = getImageProps({
        directMasterFallbackProps,
        directProps,
        height: imageHeight,
        normalizedSrc,
        listingMedia,
        trustedListingMedia,
        unoptimized,
        width,
    });

    return (
        <>
            <img
                alt={alt}
                {...optimizedProps}
                decoding="async"
                className={className}
                {...imgRest}
                ref={checkAlreadyErrored}
                loading={priority ? "eager" : loading}
                {...(priority ? { fetchPriority: "high" } : {})}
                onLoad={(event) => {
                    if (loadingPlaceholder) setLoadedSrc(normalizedSrc);
                    imageOnLoad?.(event);
                }}
                onError={(event) =>
                    handleVercelImageError(event, imageOnError, {
                        directMasterFallbackProps,
                        directProps,
                        normalizedSrc,
                        setErroredSrc,
                        setFailedDirectSrc,
                    })
                }
            />
            {loadingPlaceholder && loadedSrc !== normalizedSrc && (
                <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-havanese"
                >
                    {loadingPlaceholder}
                </span>
            )}
        </>
    );
}
